import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import { Add, Remove, Download } from '@mui/icons-material';

const DkgRequestDialog = ({
  open,
  onClose,
  dkgSelectedDeposit,
  dkgNodeStatuses,
  dkgV2Ready,
  dkgDenominations,
  dkgWithdrawalRequests,
  dkgWithdrawalProgress,
  keyRequestLoading,
  supportedChains,
  validTargets = [],
  mappingsLoading,
  onUpdateRequest,
  onAddRequest,
  onRemoveRequest,
  onExecute,
  isDark,
  isMobile,
}) => {
  const { filteredChains, tokensByChain } = useMemo(() => {
    if (!validTargets.length) {
      return { filteredChains: supportedChains, tokensByChain: {} };
    }
    const validChainNames = new Set(validTargets.map(t => t.chain));
    const filtered = supportedChains.filter(c => validChainNames.has(c.chain_name));
    const byChain = {};
    for (const t of validTargets) {
      if (!byChain[t.chain]) byChain[t.chain] = new Set();
      byChain[t.chain].add(t.token);
    }
    const tokenMap = {};
    for (const [chain, tokens] of Object.entries(byChain)) {
      tokenMap[chain] = [...tokens];
    }
    return { filteredChains: filtered, tokensByChain: tokenMap };
  }, [validTargets, supportedChains]);

  const totalRequested = dkgWithdrawalRequests.reduce((sum, r) => sum + (parseFloat(r.denomination) || 0), 0);
  const exceedsDeposit = dkgSelectedDeposit && totalRequested > dkgSelectedDeposit.amount;
  const validCount = dkgWithdrawalRequests.filter(r => r.denomination && parseFloat(r.denomination) > 0).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ bgcolor: 'rgba(34, 197, 94, 0.1)', borderBottom: '1px solid rgba(34, 197, 94, 0.2)' }}>
        Save Keys
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body2" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)', mb: 2 }}>
          Request keys and save them for later withdrawal. Use this for maximum privacy — withdraw from a different device or wallet at any time.
        </Typography>

        {!dkgV2Ready && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            DKG nodes not ready (need 3+ of 5 nodes online)
          </Alert>
        )}

        {/* Deposit Info — compact */}
        <Box sx={{ bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)', p: 1.5, borderRadius: 1, mb: 2 }}>
          <Typography variant="body2">
            <strong>{dkgSelectedDeposit?.amount} {dkgSelectedDeposit?.metadata?.token || 'ETH'}</strong>
            <Typography component="span" variant="caption" sx={{ ml: 1, fontFamily: 'monospace', color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
              {dkgSelectedDeposit?.txHash?.slice(0, 10)}...{dkgSelectedDeposit?.txHash?.slice(-6)}
            </Typography>
          </Typography>
        </Box>

        {/* Key configuration rows */}
        {dkgWithdrawalRequests.map((request, index) => (
          <Box key={index} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 1.5, alignItems: 'flex-start' }}>
            <FormControl sx={{ flex: 1, minWidth: 120 }} size="small">
              <InputLabel>Chain</InputLabel>
              <Select
                value={request.chain}
                onChange={(e) => onUpdateRequest(index, 'chain', e.target.value)}
                label="Chain"
                disabled={keyRequestLoading}
              >
                {filteredChains.map(chain => (
                  <MenuItem key={chain.chain_name} value={chain.chain_name}>{chain.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 90 }} size="small">
              <InputLabel>Token</InputLabel>
              <Select
                value={request.token}
                onChange={(e) => onUpdateRequest(index, 'token', e.target.value)}
                label="Token"
                disabled={keyRequestLoading}
              >
                {(() => {
                  const validTokens = tokensByChain[request.chain];
                  if (validTokens?.length) return validTokens.map(token => <MenuItem key={token} value={token}>{token}</MenuItem>);
                  const sc = supportedChains.find(c => c.chain_name === request.chain);
                  if (!sc?.supported_tokens?.length) return <MenuItem value="" disabled>-</MenuItem>;
                  return sc.supported_tokens.map(token => <MenuItem key={token} value={token}>{token}</MenuItem>);
                })()}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 100 }} size="small">
              <InputLabel>Amount</InputLabel>
              <Select
                value={request.denomination}
                onChange={(e) => onUpdateRequest(index, 'denomination', e.target.value)}
                label="Amount"
                disabled={keyRequestLoading}
              >
                {(() => {
                  const chainDenoms = dkgDenominations[request.chain] || [];
                  const filtered = chainDenoms.filter(d => d.tokenSymbol === request.token);
                  if (filtered.length === 0) return <MenuItem value="" disabled>No amounts</MenuItem>;
                  const seen = new Set();
                  return filtered.filter(d => {
                    const key = `${d.denomination}-${d.tokenSymbol}`;
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                  }).map(d => (
                    <MenuItem key={`${d.denomination}-${d.tokenSymbol}`} value={d.denomination.toString()}>
                      {d.denomination}
                    </MenuItem>
                  ));
                })()}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 0.5, alignSelf: { xs: 'flex-end', sm: 'center' } }}>
              {dkgWithdrawalRequests.length > 1 && (
                <IconButton onClick={() => onRemoveRequest(index)} color="error" size="small" disabled={keyRequestLoading}>
                  <Remove />
                </IconButton>
              )}
              {index === dkgWithdrawalRequests.length - 1 && (
                <IconButton onClick={onAddRequest} color="primary" size="small" disabled={keyRequestLoading}>
                  <Add />
                </IconButton>
              )}
            </Box>
          </Box>
        ))}

        {/* Total */}
        <Typography variant="caption" sx={{
          color: exceedsDeposit ? '#ef4444' : (isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'),
          display: 'block', mb: 2,
        }}>
          Total: {totalRequested.toFixed(4)} / {dkgSelectedDeposit?.amount} {dkgSelectedDeposit?.metadata?.token || 'ETH'}
        </Typography>

        {/* Progress */}
        {dkgWithdrawalProgress && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)', borderRadius: 1, mb: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">{dkgWithdrawalProgress.message}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={keyRequestLoading}>Cancel</Button>
        <Button
          onClick={onExecute}
          variant="contained"
          disabled={keyRequestLoading || !dkgV2Ready || validCount === 0 || exceedsDeposit}
          startIcon={keyRequestLoading ? <CircularProgress size={16} /> : <Download />}
          sx={{
            background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
            '&:hover': { background: 'linear-gradient(90deg, #16a34a 0%, #22c55e 100%)' },
          }}
        >
          {keyRequestLoading ? 'Processing...' : `Save ${validCount} Key${validCount !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DkgRequestDialog;
